"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronRight } from "lucide-react";

interface PathItem {
  label: string;
  href?: string;
}

interface BreadcrumbProps {
  title: string;
  paths: PathItem[];
}

export default function Breadcrumb({ title, paths }: BreadcrumbProps) {
  return (
    <>
      {/* Hero Header Section */}
      <section className="relative w-full h-[400px] lg:h-[450px] flex flex-col items-center justify-center overflow-hidden font-sans mt-[70px] lg:mt-[84px] bg-black">
        {/* Background Image & Overlay */}
        <div className="absolute inset-0 z-0 opacity-40">
          <Image
            src="/images/breadcrum-img.jpg"
            alt={title}
            fill
            sizes="100vw"
            priority
            className="object-cover object-center"
          />
        </div>
        
        {/* Decorative Corners */}
        <div className="absolute top-0 left-0 w-8 h-8 border-t border-l border-white/20 z-10 m-12" />
        <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-white/20 z-10 m-12" />
        <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-white/20 z-10 m-12" />
        <div className="absolute bottom-0 right-0 w-8 h-8 border-b border-r border-white/20 z-10 m-12" />

        {/* Title Content */}
        <div className="relative z-20 container max-w-[1700px] mx-auto px-4 text-center mt-10">
          {/* Yellow decorative dash */}
          <div className="w-12 h-[2px] bg-[#FFCD00] mx-auto mb-6" />
          
          <h3 className="text-[28px] sm:text-[36px] md:text-[44px] lg:text-[50px] font-bold text-white uppercase tracking-wider leading-tight drop-shadow-lg">
            {title}
          </h3>
        </div>
        
        {/* Scroll Down To Discover */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 bg-white px-8 py-3 rounded-t-[10px] flex items-center gap-3 z-20 shadow-lg">
           <div className="w-5 h-8 border-2 border-black rounded-full p-1 flex justify-center relative">
             <div className="w-1 h-2 bg-black rounded-full animate-bounce mt-1" />
           </div>
           <span className="text-[9px]  lg:text-[11px]  font-bold text-black uppercase tracking-wider">
             Scroll Down To Discover
           </span>
        </div>
      </section>

      {/* Breadcrumb Path Bar */}
      <div className="relative z-30 w-full bg-[#f9f9f9] border-b border-[#eee] pt-8 pb-4 font-sans">
        <div className="w-full max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-[50px]">
          <div className="flex items-center justify-between w-full border border-[#eee] bg-white">
            <div className="flex items-center gap-2 text-[10px] font-extrabold uppercase text-[#1e1e1e] tracking-wider px-6 py-4">
              {paths.map((path, index) => {
                const isLast = index === paths.length - 1;

                return (
                  <React.Fragment key={index}>
                    {path.href && !isLast ? (
                      <Link 
                        href={path.href}
                        className="hover:text-[#FFCD00] transition-colors duration-200"
                      >
                        {path.label}
                      </Link>
                    ) : (
                      <span className="text-[#666]">
                        {path.label}
                      </span>
                    )}

                    {!isLast && (
                      <ChevronRight className="w-3 h-3 text-[#ccc] shrink-0 mx-1" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            
            <div className="w-[60px] h-[50px] bg-[#FFCD00] flex items-center justify-center shrink-0 cursor-pointer">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
